import { getProducts } from '../components/Product/productData';
import { ROUTE_METADATA } from '../routeMetadata';

export interface ProductAnalyticsContext {
  page_path: string;
  product_category: string;
  product_name: string;
}

const PRODUCT_PATH_ALIASES: Record<string, string> = {
  '/bill-splitter': '/split',
  '/bills': '/split',
  '/codenames': '/cn',
  '/count-artists': '/artists',
  '/crime-map': '/crime',
  '/departure': '/mta',
  '/discover-movies': '/movies',
  '/dj-sets-playlist': '/djsets',
  '/fan-since': '/fansince',
  '/filter-playlist': '/filter',
  '/find-duplicates': '/dupes',
  '/find-events': '/events',
  '/find-new-music': '/discover',
  '/fire-simulator': '/fire',
  '/flight-tracker': '/flights',
  '/playlist-analysis': '/dna',
  '/playlist-intersection': '/overlap',
  '/playlist-segments': '/segments',
  '/poker-trainer': '/poker',
  '/prompt-helper': '/prompt',
  '/randomly-selected': '/flights',
  '/rsu-simulator': '/rsu',
  '/selected': '/flights',
  '/set-playlist': '/sets',
  '/split-bills': '/split',
  '/sr-ai': '/chat',
  '/ssss': '/flights',
  '/twitter-chat': '/tweetgpt',
  '/watchlists': '/letterboxd',
};

const routePaths = Object.keys(ROUTE_METADATA).sort((left, right) => right.length - left.length);

const matchesPath = (pathname: string, routePath: string) =>
  pathname === routePath || pathname.startsWith(`${routePath}/`);

export const getAnalyticsPath = (pathname: string) => {
  const normalizedPath = pathname.endsWith('/') && pathname !== '/'
    ? pathname.slice(0, -1)
    : pathname;
  return routePaths.find((routePath) => matchesPath(normalizedPath, routePath)) ?? '/unknown';
};

/** The Vercel event with the paste id cut from its URL, because a paste link is the key to the paste. */
export const withoutPasteId = <T extends { url: string }>(event: T): T => {
  const url = new URL(event.url);
  if (!url.pathname.startsWith('/p/')) return event;
  url.pathname = '/p';
  url.hash = '';
  return { ...event, url: url.toString() };
};

export const getProductAnalyticsContext = (
  pathname: string
): ProductAnalyticsContext | null => {
  const analyticsPath = getAnalyticsPath(pathname);
  const canonicalPath = Object.entries(PRODUCT_PATH_ALIASES).find(([alias]) =>
    matchesPath(analyticsPath, alias)
  )?.[1] ?? analyticsPath;
  const product = getProducts().find(({ to }) => matchesPath(canonicalPath, to));

  if (!product) return null;

  return {
    page_path: analyticsPath,
    product_category: product.category.toLowerCase(),
    product_name: product.primary,
  };
};
